import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { useAtomValue, useSetAtom, useStore } from "jotai";
import { useEffect, useRef } from "react";

import {
  LanguageSelect,
  SupportedLanguage,
} from "@whoosh/design-system/src/components/controls";

import { useFlags } from "../../utils/launchDarkly";
import { useLanguagePreferenceStore } from "./LanguagePreferenceProvider";
import {
  authenticatedLanguagePreferenceAtom,
  effectiveLanguageAtom,
  normalizeLanguage,
  storedLanguagePreferenceAtom,
} from "./atoms";
import { useUpdateLanguagePreference } from "./useUpdateLanguagePreference";

type LanguageSelectContainerProps = {
  store?: ReturnType<typeof useStore>;
  syncToUserProfile?: boolean;
  testID?: string;
};

const useLanguageSelectAccessibilityLabels = () => {
  useLingui();

  return {
    accessibilityLabel: t`Select language`,
    optionAccessibilityLabels: {
      en: t`Select English`,
      fr: t`Select French`,
    },
  };
};

export const usePatronLanguageSelectorEnabled = () => {
  const { patronLanguageSelector } = useFlags();

  return patronLanguageSelector === true;
};

export const LanguageSelectContainer = ({
  ...props
}: LanguageSelectContainerProps) => {
  const languageSelectorEnabled = usePatronLanguageSelectorEnabled();

  // LaunchDarkly's `patron-language-selector` flag is intentionally default-off.
  if (!languageSelectorEnabled) {
    return null;
  }

  return <EnabledLanguageSelectContainer {...props} />;
};

const EnabledLanguageSelectContainer = ({
  store,
  syncToUserProfile = false,
  testID,
}: LanguageSelectContainerProps) => {
  const appLanguagePreferenceStore = useLanguagePreferenceStore();
  const nearestStore = useStore();
  const resolvedStore = store ?? appLanguagePreferenceStore ?? nearestStore;
  const storeOptions = { store: resolvedStore };
  const language =
    normalizeLanguage(useAtomValue(effectiveLanguageAtom, storeOptions)) ??
    "en";
  const setStoredLanguage = useSetAtom(
    storedLanguagePreferenceAtom,
    storeOptions
  );
  const accessibilityLabels = useLanguageSelectAccessibilityLabels();

  if (syncToUserProfile) {
    return (
      <AuthenticatedLanguageSelect
        {...accessibilityLabels}
        language={language}
        store={resolvedStore}
        testID={testID}
      />
    );
  }

  return (
    <LanguageSelect
      {...accessibilityLabels}
      onChange={setStoredLanguage}
      testID={testID}
      value={language}
    />
  );
};

type AuthenticatedLanguageSelectProps = {
  accessibilityLabel: string;
  language: SupportedLanguage;
  optionAccessibilityLabels: Record<SupportedLanguage, string>;
  store: ReturnType<typeof useStore>;
  testID?: string;
};

const AuthenticatedLanguageSelect = ({
  accessibilityLabel,
  language,
  optionAccessibilityLabels,
  store,
  testID,
}: AuthenticatedLanguageSelectProps) => {
  const storeOptions = { store };
  const storedLanguage = useAtomValue(
    storedLanguagePreferenceAtom,
    storeOptions
  );
  const setStoredLanguage = useSetAtom(
    storedLanguagePreferenceAtom,
    storeOptions
  );
  const authenticatedLanguage = useAtomValue(
    authenticatedLanguagePreferenceAtom,
    storeOptions
  );
  const setAuthenticatedLanguage = useSetAtom(
    authenticatedLanguagePreferenceAtom,
    storeOptions
  );
  const [updateLanguagePreference, isUpdatingLanguagePreference] =
    useUpdateLanguagePreference();
  const isActiveRef = useRef(true);

  useEffect(() => {
    isActiveRef.current = true;

    return () => {
      isActiveRef.current = false;
    };
  }, []);

  const handleChange = (nextLanguage: SupportedLanguage) => {
    if (isUpdatingLanguagePreference) {
      return;
    }

    const rollbackLanguagePreference = () => {
      if (
        !isActiveRef.current ||
        store.get(authenticatedLanguagePreferenceAtom) !== nextLanguage
      ) {
        return;
      }

      setStoredLanguage(storedLanguage);
      setAuthenticatedLanguage(authenticatedLanguage);
    };

    setStoredLanguage(nextLanguage);
    setAuthenticatedLanguage(nextLanguage);
    updateLanguagePreference({
      variables: { input: { preferredLocale: nextLanguage } },
      onCompleted: () => {},
      onCompletedErrorCustom: (errors) => {
        rollbackLanguagePreference();
        return errors;
      },
      onErrorCustom: rollbackLanguagePreference,
      onPayloadError: rollbackLanguagePreference,
    });
  };

  return (
    <LanguageSelect
      accessibilityLabel={accessibilityLabel}
      onChange={handleChange}
      optionAccessibilityLabels={optionAccessibilityLabels}
      testID={testID}
      value={language}
    />
  );
};
